import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { useClassSettingsModal } from '../contexts/ClassSettingsModalContext';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ClassScheduleRow,
  ClassScheduleRowTimes,
  ClassScheduleSettings,
  SectionTimeMode,
  WeekdayKey,
  WeekdayMaxPeriod,
  getClassScheduleSettings,
  PageSectionLayout,
  saveClassScheduleSettings,
} from '../services/sectionLayoutService';
import {
  applyTimesToRows,
  extractRowTimes,
  hasCommonScheduleStartTimes,
  seedAllWeekdayRowTimes,
  seedWeekdayRowTimesFromRows,
} from '../utils/classScheduleWeekdayTimes';
import ClassScheduleTimeInput from '../components/classSchedule/ClassScheduleTimeInput';
import ClockOutlineIcon from '../components/common/ClockOutlineIcon';
import AutoStatusToast from '../components/common/AutoStatusToast';
import ProgramImportLearningDisabledHint from '../components/program/ProgramImportLearningDisabledHint';
import ClassScheduleLeaderSectionImportModal from '../components/timetable/ClassScheduleLeaderSectionImportModal';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import { useTeamIdForLeaderImport } from '../hooks/useTeamIdForLeaderImport';
import type { TeamLeaderClassScheduleImportPayload } from '../services/classScheduleTeamLeaderImportService';
import {
  ANNUAL_PREREQUISITE_REFRESH_EVENT,
  emitAnnualPrerequisiteRefresh,
  type AnnualPrerequisiteRefreshDetail,
} from '../utils/annualPrerequisiteRefresh';
import { Grade } from '../types/domain';
import AiPresetImportModal from '../components/aiPreset/AiPresetImportModal';
import AiPresetOpenButton from '../components/aiPreset/AiPresetOpenButton';
import { gradeNumbersFromClassName, guessSchoolYear, type AiScheduleResult } from '../utils/aiPresetExtract';

interface ClassSchedulePageProps {
  workspaceId: string;
  currentGradeId?: string;
  grades?: Grade[];
  onNavGradeChange?: (gradeId: string) => void;
}

const MAX_SECTIONS = 6;
const WEEKDAY_COLUMNS: Array<{ key: WeekdayKey; label: string }> = [
  { key: 'mon', label: '월' },
  { key: 'tue', label: '화' },
  { key: 'wed', label: '수' },
  { key: 'thu', label: '목' },
  { key: 'fri', label: '금' },
];

function parseClassName(raw: string): { title: string; grades: number[] } {
  const [base, rest] = raw.split('::');
  if (!rest) return { title: raw, grades: [] };
  const nums = rest
    .split(',')
    .map((v) => Number(v))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 6);
  return { title: base, grades: nums };
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function createDefaultRows(): ClassScheduleRow[] {
  return Array.from({ length: 10 }, () => ({
    kind: 'class',
    name: '',
    startTime: '',
    durationMinutes: '',
    endTime: '',
  }));
}

function createDefaultWeekdayMax(): WeekdayMaxPeriod {
  return { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0 };
}

function toMinutes(hhmm: string): number | null {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

function toHHMM(totalMin: number): string {
  const wrapped = ((totalMin % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function recalcRow(next: ClassScheduleRow, changed: 'start' | 'duration' | 'end'): ClassScheduleRow {
  const startMin = toMinutes(next.startTime);
  const endMin = toMinutes(next.endTime);
  const durationNum = parseInt(next.durationMinutes || '', 10);
  const hasDuration = Number.isFinite(durationNum) && durationNum >= 0;

  if (changed === 'start') {
    if (startMin != null && hasDuration) {
      return { ...next, endTime: toHHMM(startMin + durationNum) };
    }
    if (startMin != null && endMin != null) {
      return { ...next, durationMinutes: String(((endMin - startMin + 1440) % 1440)) };
    }
    return next;
  }
  if (changed === 'duration') {
    if (startMin != null && hasDuration) {
      return { ...next, endTime: toHHMM(startMin + durationNum) };
    }
    return next;
  }
  if (changed === 'end') {
    if (startMin != null && endMin != null) {
      return { ...next, durationMinutes: String(((endMin - startMin + 1440) % 1440)) };
    }
    return next;
  }
  return next;
}

function recalcRowTimes(
  times: ClassScheduleRowTimes,
  changed: 'start' | 'duration' | 'end'
): ClassScheduleRowTimes {
  return extractRowTimes(
    recalcRow({ kind: 'class', name: '', ...times }, changed)
  );
}

function getDisplayRowsForSection(
  baseRows: ClassScheduleRow[],
  mode: SectionTimeMode,
  editingDay: WeekdayKey,
  weekdayTimesForSection: Partial<Record<WeekdayKey, ClassScheduleRowTimes[]>> | undefined
): ClassScheduleRow[] {
  if (mode !== 'weekday') return baseRows;
  const dayTimes = weekdayTimesForSection?.[editingDay];
  if (!dayTimes?.length) return baseRows;
  return applyTimesToRows(baseRows, dayTimes);
}

function applyAutoKindNames(rows: ClassScheduleRow[]): ClassScheduleRow[] {
  let classCount = 0;
  let afterSchoolCount = 0;
  return rows.map((row) => {
    if (row.kind === 'class') {
      classCount += 1;
      return { ...row, name: `${classCount}교시` };
    }
    if (row.kind === 'after_school') {
      afterSchoolCount += 1;
      return { ...row, name: `방과후${afterSchoolCount}` };
    }
    return row;
  });
}

const SortableSectionGroup: React.FC<{
  id: string;
  children: (attributes: any, listeners: any) => React.ReactNode;
}> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: { type: 'section' },
  });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={'cp-grade-track-section-group' + (isDragging ? ' cp-section-dragging' : '')}
    >
      {children(attributes, listeners)}
    </div>
  );
};

const ClassSchedulePage: React.FC<ClassSchedulePageProps> = ({
  workspaceId,
  currentGradeId = '',
  grades = [],
  onNavGradeChange,
}) => {
  const [loading, setLoading] = useState(true);
  const { toastState: saveToastState, showSaving, showSuccess, showError } = useAutoStatusToast(3000);
  const [sections, setSections] = useState<PageSectionLayout[]>([{ id: 'base', gradeIds: [] }]);
  const [sectionRows, setSectionRows] = useState<Record<string, ClassScheduleRow[]>>({
    base: createDefaultRows(),
  });
  const [sectionWeekdayMax, setSectionWeekdayMax] = useState<Record<string, WeekdayMaxPeriod>>({
    base: createDefaultWeekdayMax(),
  });
  const [sectionTimeMode, setSectionTimeMode] = useState<Record<string, SectionTimeMode>>({
    base: 'common',
  });
  const [sectionWeekdayRowTimes, setSectionWeekdayRowTimes] = useState<
    Record<string, Partial<Record<WeekdayKey, ClassScheduleRowTimes[]>>>
  >({});
  const [sectionEditingWeekday, setSectionEditingWeekday] = useState<Record<string, WeekdayKey>>({
    base: 'mon',
  });
  const [classPickerSectionId, setClassPickerSectionId] = useState<string | null>(null);
  const [removeClassContext, setRemoveClassContext] = useState<{
    sectionId: string;
    gradeId: string;
  } | null>(null);
  const [deleteSectionContext, setDeleteSectionContext] = useState<string | null>(null);
  const [resetSectionContext, setResetSectionContext] = useState<string | null>(null);
  const [copySectionContext, setCopySectionContext] = useState<string | null>(null);
  const [copyModalSelectedGradeIds, setCopyModalSelectedGradeIds] = useState<string[]>([]);
  const teamIdForLeaderImport = useTeamIdForLeaderImport(workspaceId);
  const [leaderImportTargetSectionId, setLeaderImportTargetSectionId] = useState<string | null>(null);

  const { setClassScheduleDirty, saveClassScheduleRef } = useContext(GradeTrackLeaveContext);
  const classSettingsModal = useClassSettingsModal();

  const classPickerAvailableGrades = useMemo(() => {
    if (!classPickerSectionId) return [];
    return grades.filter((g) => {
      const sec = sections.find((s) => s.id === classPickerSectionId);
      const inThis = sec?.gradeIds.includes(g.id);
      const inAny = sections.some(
        (s) => s.id !== classPickerSectionId && s.gradeIds.includes(g.id)
      );
      return !inThis && !inAny;
    });
  }, [grades, sections, classPickerSectionId]);

  const lastSavedSnapshotRef = useRef<string>('');
  const getSnapshotRef = useRef<() => string>(() => '');
  const prevLoadingRef = useRef(loading);
  /**
   * 저장 직후 emit이 자기 리스너를 다시 돌리면, 기준 스냅샷 이후에 서버 재로드가 들어와
   * isDirty가 다시 true가 된다(저장 토스트 후에도 이탈 경고).
   */
  const skipSelfPrerequisiteRefreshOnceRef = useRef(false);

  const sectionSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const getSnapshot = useCallback(
    () =>
      JSON.stringify({
        sections,
        sectionRows,
        sectionWeekdayMax,
        sectionTimeMode,
        sectionWeekdayRowTimes,
      }),
    [sections, sectionRows, sectionWeekdayMax, sectionTimeMode, sectionWeekdayRowTimes]
  );
  getSnapshotRef.current = getSnapshot;
  const isDirty =
    lastSavedSnapshotRef.current !== '' && getSnapshot() !== lastSavedSnapshotRef.current;

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    getClassScheduleSettings(workspaceId)
      .then((scheduleSettings) => {
        setSections(scheduleSettings.sections);
        setSectionRows(scheduleSettings.sectionRows);
        setSectionWeekdayMax(scheduleSettings.sectionWeekdayMax);
        setSectionTimeMode(
          scheduleSettings.sectionTimeMode ??
            Object.fromEntries(scheduleSettings.sections.map((s) => [s.id, 'common' as const]))
        );
        setSectionWeekdayRowTimes(scheduleSettings.sectionWeekdayRowTimes ?? {});
        setSectionEditingWeekday(
          Object.fromEntries(scheduleSettings.sections.map((s) => [s.id, 'mon' as WeekdayKey]))
        );
      })
      .catch(() => {
        // keep defaults
      })
      .finally(() => {
        setLoading(false);
      });
  }, [workspaceId]);

  /** 연간시간표 전체 가져오기 등: keep-alive에서도 서버 데이터로 갱신 */
  useEffect(() => {
    if (!workspaceId) return;
    const onRefresh = (event: Event) => {
      const detail = (event as CustomEvent<AnnualPrerequisiteRefreshDetail>).detail;
      if (!detail || detail.workspaceId !== workspaceId) return;
      if (!detail.scopes.includes('class_schedule')) return;
      if (skipSelfPrerequisiteRefreshOnceRef.current) {
        skipSelfPrerequisiteRefreshOnceRef.current = false;
        return;
      }
      setLoading(true);
      void getClassScheduleSettings(workspaceId, { skipLocalCache: true })
        .then((scheduleSettings) => {
          setSections(scheduleSettings.sections);
          setSectionRows(scheduleSettings.sectionRows);
          setSectionWeekdayMax(scheduleSettings.sectionWeekdayMax);
          setSectionTimeMode(
            scheduleSettings.sectionTimeMode ??
              Object.fromEntries(scheduleSettings.sections.map((s) => [s.id, 'common' as const]))
          );
          setSectionWeekdayRowTimes(scheduleSettings.sectionWeekdayRowTimes ?? {});
          setSectionEditingWeekday(
            Object.fromEntries(scheduleSettings.sections.map((s) => [s.id, 'mon' as WeekdayKey]))
          );
        })
        .catch(() => {
          /* keep current */
        })
        .finally(() => setLoading(false));
    };
    window.addEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onRefresh);
  }, [workspaceId]);

  useEffect(() => {
    const justStoppedLoading = prevLoadingRef.current && !loading;
    prevLoadingRef.current = loading;
    if (justStoppedLoading) {
      const id = window.setTimeout(() => {
        lastSavedSnapshotRef.current = getSnapshotRef.current();
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [loading]);

  const handleSaveAll = useCallback(async () => {
    setLoading(true);
    showSaving();
    try {
      const scheduleSettings: ClassScheduleSettings = {
        sections,
        sectionRows,
        sectionWeekdayMax,
        sectionTimeMode,
        sectionWeekdayRowTimes: Object.keys(sectionWeekdayRowTimes).length ? sectionWeekdayRowTimes : undefined,
      };
      await saveClassScheduleSettings(workspaceId, scheduleSettings);
      skipSelfPrerequisiteRefreshOnceRef.current = true;
      emitAnnualPrerequisiteRefresh(workspaceId, ['class_schedule']);
      lastSavedSnapshotRef.current = getSnapshotRef.current();
      showSuccess();
    } catch (error: any) {
      showError(error?.message ?? '저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [
    workspaceId,
    sectionRows,
    sectionWeekdayMax,
    sectionTimeMode,
    sectionWeekdayRowTimes,
    sections,
    showError,
    showSaving,
    showSuccess,
  ]);

  const handleDiscardUnsaved = useCallback(() => {
    const raw = lastSavedSnapshotRef.current;
    if (!raw) return;
    try {
      const snap = JSON.parse(raw) as {
        sections: PageSectionLayout[];
        sectionRows: Record<string, ClassScheduleRow[]>;
        sectionWeekdayMax: Record<string, WeekdayMaxPeriod>;
        sectionTimeMode?: Record<string, SectionTimeMode>;
        sectionWeekdayRowTimes?: Record<string, Partial<Record<WeekdayKey, ClassScheduleRowTimes[]>>>;
      };
      setSections(snap.sections?.length ? snap.sections : [{ id: 'base', gradeIds: [] }]);
      setSectionRows(snap.sectionRows ?? { base: createDefaultRows() });
      setSectionWeekdayMax(snap.sectionWeekdayMax ?? { base: createDefaultWeekdayMax() });
      setSectionTimeMode(snap.sectionTimeMode ?? { base: 'common' });
      setSectionWeekdayRowTimes(snap.sectionWeekdayRowTimes ?? {});
      setSectionEditingWeekday(
        Object.fromEntries((snap.sections?.length ? snap.sections : [{ id: 'base', gradeIds: [] }]).map((s) => [
          s.id,
          'mon' as WeekdayKey,
        ]))
      );
    } catch {
      return;
    }
    setClassPickerSectionId(null);
    setRemoveClassContext(null);
    setDeleteSectionContext(null);
    setCopySectionContext(null);
    setCopyModalSelectedGradeIds([]);
    setLeaderImportTargetSectionId(null);
  }, []);

  useEffect(() => {
    setClassScheduleDirty(isDirty);
    return () => setClassScheduleDirty(false);
  }, [isDirty, setClassScheduleDirty]);

  useEffect(() => {
    saveClassScheduleRef.current = { save: handleSaveAll, discard: handleDiscardUnsaved };
    return () => {
      saveClassScheduleRef.current = null;
    };
  }, [saveClassScheduleRef, handleSaveAll, handleDiscardUnsaved]);

  const copyModalAvailableGrades = grades.filter(
    (g) => !sections.some((s) => s.gradeIds.includes(g.id))
  );
  const toggleCopyModalGrade = (gradeId: string) => {
    setCopyModalSelectedGradeIds((prev) =>
      prev.includes(gradeId) ? prev.filter((id) => id !== gradeId) : [...prev, gradeId]
    );
  };

  const addSection = () => {
    if (sections.length >= MAX_SECTIONS) return;
    const id = generateId();
    setSections((prev) => [...prev, { id, gradeIds: [] }]);
    setSectionRows((prev) => ({ ...prev, [id]: createDefaultRows() }));
    setSectionWeekdayMax((prev) => ({ ...prev, [id]: createDefaultWeekdayMax() }));
    setSectionTimeMode((prev) => ({ ...prev, [id]: 'common' }));
    setSectionEditingWeekday((prev) => ({ ...prev, [id]: 'mon' }));
  };

  const removeSection = (sectionId: string) => {
    if (sections.length <= 1) return;
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
    setSectionRows((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
    setSectionWeekdayMax((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
    setSectionTimeMode((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
    setSectionWeekdayRowTimes((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
    setSectionEditingWeekday((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
    setDeleteSectionContext(null);
  };

  /** 섹션 본문·등록 클래스 초기화. DB는 저장하기 시에만 반영 */
  const resetSectionData = (sectionId: string) => {
    setSectionRows((prev) => ({ ...prev, [sectionId]: createDefaultRows() }));
    setSectionWeekdayMax((prev) => ({ ...prev, [sectionId]: createDefaultWeekdayMax() }));
    setSectionTimeMode((prev) => ({ ...prev, [sectionId]: 'common' }));
    setSectionWeekdayRowTimes((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
    setSectionEditingWeekday((prev) => ({ ...prev, [sectionId]: 'mon' }));
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, gradeIds: [] } : s))
    );
    setResetSectionContext(null);
  };

  const addClassToSection = (sectionId: string, gradeId: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId && !s.gradeIds.includes(gradeId)
          ? { ...s, gradeIds: [...s.gradeIds, gradeId] }
          : s
      )
    );
    setClassPickerSectionId(null);
    onNavGradeChange?.(gradeId);
  };

  const removeClassFromSection = (sectionId: string, gradeId: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId ? { ...s, gradeIds: s.gradeIds.filter((id) => id !== gradeId) } : s
      )
    );
    setRemoveClassContext(null);
  };

  const handleCopySectionConfirm = () => {
    const sourceId = copySectionContext;
    if (!sourceId || sections.length >= MAX_SECTIONS) return;
    if (copyModalSelectedGradeIds.length === 0) return;
    const srcIndex = sections.findIndex((s) => s.id === sourceId);
    if (srcIndex < 0) return;
    const newId = generateId();
    const nextSections = [...sections];
    nextSections.splice(srcIndex + 1, 0, { id: newId, gradeIds: [...copyModalSelectedGradeIds] });
    const srcRows = sectionRows[sourceId] ?? createDefaultRows();
    const srcWeekdayMax = sectionWeekdayMax[sourceId] ?? createDefaultWeekdayMax();
    const srcTimeMode = sectionTimeMode[sourceId] ?? 'common';
    const srcWeekdayTimes = sectionWeekdayRowTimes[sourceId];
    setSections(nextSections);
    setSectionRows((prev) => ({
      ...prev,
      [newId]: srcRows.map((r) => ({ ...r })),
    }));
    setSectionWeekdayMax((prev) => ({
      ...prev,
      [newId]: { ...srcWeekdayMax },
    }));
    setSectionTimeMode((prev) => ({ ...prev, [newId]: srcTimeMode }));
    if (srcWeekdayTimes) {
      const copied: Partial<Record<WeekdayKey, ClassScheduleRowTimes[]>> = {};
      WEEKDAY_COLUMNS.forEach(({ key }) => {
        const dayRows = srcWeekdayTimes[key];
        if (dayRows) copied[key] = dayRows.map((t) => ({ ...t }));
      });
      setSectionWeekdayRowTimes((prev) => ({ ...prev, [newId]: copied }));
    }
    setSectionEditingWeekday((prev) => ({ ...prev, [newId]: sectionEditingWeekday[sourceId] ?? 'mon' }));
    setCopySectionContext(null);
    setCopyModalSelectedGradeIds([]);
  };

  const [aiSectionId, setAiSectionId] = useState<string | null>(null);

  /** AI 자료 입력 반영: 1~N교시(시각) + 나머지 행은 방과후(시간 없음), 활동 행은 만들지 않음 */
  const applyAiSchedule = (sectionId: string, r: AiScheduleResult) => {
    const dur = (a: string, b: string) => {
      const x = toMinutes(a);
      const y = toMinutes(b);
      return x != null && y != null && y > x ? String(y - x) : '';
    };
    const classRows: ClassScheduleRow[] = r.periods.slice(0, 10).map((p) => ({
      kind: 'class',
      name: '',
      startTime: p.start_time,
      durationMinutes: dur(p.start_time, p.end_time),
      endTime: p.end_time,
    }));
    const rows = applyAutoKindNames([
      ...classRows,
      ...Array.from({ length: 10 - classRows.length }, (): ClassScheduleRow => ({
        kind: 'after_school',
        name: '',
        startTime: '',
        durationMinutes: '',
        endTime: '',
      })),
    ]);
    const cap = (n: number) => Math.max(0, Math.min(classRows.length || 10, n));
    const w = r.weekday_periods;
    setSectionRows((prev) => ({ ...prev, [sectionId]: rows }));
    setSectionWeekdayMax((prev) => ({ ...prev, [sectionId]: { mon: cap(w.mon), tue: cap(w.tue), wed: cap(w.wed), thu: cap(w.thu), fri: cap(w.fri) } }));
    setSectionTimeMode((prev) => ({ ...prev, [sectionId]: 'common' }));
    showSuccess('AI 자료를 반영했습니다. 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.');
  };
  const aiDefaultGradeFor = (sectionId: string): number => {
    const ids = sections.find((x) => x.id === sectionId)?.gradeIds ?? [];
    const gid = ids.includes(currentGradeId) ? currentGradeId : ids[0];
    const g = grades.find((x) => x.id === gid) ?? grades.find((x) => x.id === currentGradeId);
    return gradeNumbersFromClassName(g?.name ?? '')[0] ?? 1;
  };

  const applyLeaderClassScheduleImport = (
    sectionId: string,
    payload: TeamLeaderClassScheduleImportPayload
  ) => {
    const importedRows = applyAutoKindNames(payload.rows.map((row) => ({ ...row }))).slice(0, 10);
    const importedWeekdayMax = { ...payload.weekday_max };
    setSectionRows((prev) => ({ ...prev, [sectionId]: importedRows }));
    setSectionWeekdayMax((prev) => ({ ...prev, [sectionId]: importedWeekdayMax }));

    /** 사이드바에서 선택된 클래스가 비어 있고 다른 섹션에 없으면 이 섹션에 자동 등록 */
    const focusGradeId = currentGradeId.trim();
    const canAutoRegister =
      !!focusGradeId &&
      grades.some((g) => g.id === focusGradeId) &&
      !sections.some((s) => s.gradeIds.includes(focusGradeId));
    if (canAutoRegister) {
      setSections((prev) =>
        prev.map((s) =>
          s.id === sectionId ? { ...s, gradeIds: [...s.gradeIds, focusGradeId] } : s
        )
      );
    }

    showSuccess('팀장 수업시간 설정을 가져왔습니다. 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.');
  };

  const handleSectionDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (active.data.current?.type !== 'section') return;
    const oldIndex = sections.findIndex((s) => s.id === active.id);
    const newIndex = sections.findIndex((s) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setSections((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const setSectionTimeModeForSection = (sectionId: string, mode: SectionTimeMode) => {
    if (mode === 'weekday') {
      const rows = sectionRows[sectionId] ?? createDefaultRows();
      if (!hasCommonScheduleStartTimes(rows)) return;
      setSectionWeekdayRowTimes((prev) => {
        if (prev[sectionId] && Object.keys(prev[sectionId]).length) return prev;
        return { ...prev, [sectionId]: seedAllWeekdayRowTimes(rows) };
      });
      setSectionTimeMode((prev) => ({ ...prev, [sectionId]: 'weekday' }));
      return;
    }
    /** 공통(sectionRows)과 요일별(sectionWeekdayRowTimes)은 별도 저장 — 전환 시 서로 덮어쓰지 않음 */
    setSectionTimeMode((prev) => ({ ...prev, [sectionId]: 'common' }));
  };

  const updateSectionRow = (
    sectionId: string,
    rowIndex: number,
    patch: Partial<ClassScheduleRow>,
    changed?: 'start' | 'duration' | 'end'
  ) => {
    const isTimePatch =
      !!changed ||
      'startTime' in patch ||
      'durationMinutes' in patch ||
      'endTime' in patch;
    const mode = sectionTimeMode[sectionId] ?? 'common';
    const editingDay = sectionEditingWeekday[sectionId] ?? 'mon';

    if (isTimePatch && mode === 'weekday') {
      setSectionWeekdayRowTimes((prev) => {
        const baseRows = sectionRows[sectionId] ?? createDefaultRows();
        const sectionDays = {
          ...(prev[sectionId] ?? seedAllWeekdayRowTimes(baseRows)),
        };
        const dayRows = [...(sectionDays[editingDay] ?? seedWeekdayRowTimesFromRows(baseRows))];
        const currentTimes =
          dayRows[rowIndex] ?? extractRowTimes(baseRows[rowIndex] ?? createDefaultRows()[0]);
        let nextTimes: ClassScheduleRowTimes = { ...currentTimes, ...patch };
        if (changed) nextTimes = recalcRowTimes(nextTimes, changed);
        dayRows[rowIndex] = nextTimes;
        sectionDays[editingDay] = dayRows;
        return { ...prev, [sectionId]: sectionDays };
      });
      return;
    }

    setSectionRows((prev) => {
      const rows = prev[sectionId] ?? createDefaultRows();
      const nextRows = [...rows];
      const nextRow = { ...nextRows[rowIndex], ...patch };
      nextRows[rowIndex] = changed ? recalcRow(nextRow, changed) : nextRow;
      return { ...prev, [sectionId]: applyAutoKindNames(nextRows) };
    });
  };

  const updateSectionWeekdayMax = (sectionId: string, day: WeekdayKey, period: number) => {
    setSectionWeekdayMax((prev) => {
      const current = prev[sectionId] ?? createDefaultWeekdayMax();
      return {
        ...prev,
        [sectionId]: { ...current, [day]: period },
      };
    });
  };

  /** 수업시간은 항상 전체 섹션을 보여 주고, 선택 클래스 구독 여부는 사이드 메뉴 원형으로 표시 */
  const visibleSections = sections;

  return (
    <section className="cp-timetable-hub-page">
      <div className="cp-page-title-row">
        <div>
          <h2>수업시간 및 요일별 기준 수업시수</h2>
        </div>
        <button
          type="button"
          className="cp-btn-save-all"
          onClick={handleSaveAll}
          disabled={loading || !isDirty}
        >
          {isDirty ? '저장하기' : '변경사항 없음'}
        </button>
      </div>

      <DndContext sensors={sectionSensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
        <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {visibleSections.map((section) => {
            const visibleGradeIds = section.gradeIds;
            const baseRows = (sectionRows[section.id] ?? createDefaultRows()).slice(0, 10);
            const timeMode = sectionTimeMode[section.id] ?? 'common';
            const editingWeekday = sectionEditingWeekday[section.id] ?? 'mon';
            const rows = getDisplayRowsForSection(
              baseRows,
              timeMode,
              editingWeekday,
              sectionWeekdayRowTimes[section.id]
            );
            const weekdayMax = sectionWeekdayMax[section.id] ?? createDefaultWeekdayMax();
            const canUseWeekdayMode =
              timeMode === 'weekday' || hasCommonScheduleStartTimes(baseRows);
            const weekdayModeHint = canUseWeekdayMode
              ? null
              : '공통설정 시각을 먼저 설정해주세요.';
            return (
              <SortableSectionGroup key={section.id} id={section.id}>
                {(dragAttributes, dragListeners) => (
      <article className="cp-card">
                    <div className="cp-track-section-header cp-track-section-header-with-class cp-grade-track-header-row cp-class-schedule-section-header cp-section-mhead">
                      <div className="card-summary pill cp-track-class-pill">클래스 공유</div>
                      <h3>수업시간 및 요일별 기준 수업시수 설정</h3>
                      <button
                        type="button"
                        className="cp-program-color-class-add-btn"
                        onClick={() => setClassPickerSectionId(section.id)}
                        aria-label="클래스 등록"
                        title="클래스 등록"
                      >
                        + 클래스 등록
                      </button>
                      {section.gradeIds.length === 0 ? (
                        <span className="cp-section-class-empty-hint">
                          * 데이터를 먼저 입력한 뒤 클래스를 등록할 수 있습니다.
                        </span>
                      ) : (
                        visibleGradeIds.map((gid) => {
                          const grade = grades.find((g) => g.id === gid);
                          const parsed = grade ? parseClassName(grade.name) : { title: gid, grades: [] };
                          return (
                            <div
                              key={gid}
                              className={
                                'cp-grade-track-class-block' +
                                (gid === currentGradeId ? ' is-nav-focus' : ' is-nav-muted')
                              }
                            >
                              <span className="cp-grade-track-class-block-label">{parsed.title}</span>
                              {parsed.grades.map((num) => (
                                <span key={num} className="class-grade-chip-display">
                                  {num}
                                </span>
                              ))}
                              <button
                                type="button"
                                className="cp-top-right-remove-btn"
                                onClick={() => setRemoveClassContext({ sectionId: section.id, gradeId: gid })}
                                aria-label="클래스 제거"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })
                      )}
                      <div className="cp-grade-track-section-header-actions">
                        <AiPresetOpenButton onClick={() => setAiSectionId(section.id)} />
                        <button
                          type="button"
                          className="cp-program-color-section-copy-btn"
                          onClick={() => setResetSectionContext(section.id)}
                          aria-label="섹션 초기화"
                          title="섹션 초기화"
                        >
                          <img src="/reset.svg" alt="" className="track-icon" />
                        </button>
                        <button
                          type="button"
                          className="cp-program-color-section-copy-btn"
                          onClick={() => {
                            setCopySectionContext(section.id);
                            setCopyModalSelectedGradeIds([]);
                          }}
                          aria-label="섹션 복사"
                          title="섹션 복사"
                        >
                          <img src="/Copy.svg" alt="복사" className="track-icon" />
                        </button>
                        {teamIdForLeaderImport ? (
                          <button
                            type="button"
                            className="cp-program-color-section-copy-btn"
                            onClick={() => setLeaderImportTargetSectionId(section.id)}
                            aria-label="팀장 수업시간 설정 가져오기"
                            title="팀장 수업시간 설정 가져오기"
                          >
                            <img src="/Download.svg" alt="" className="track-icon" />
                          </button>
                        ) : null}
                        {sections.length >= 2 && (
                          <>
                            <button
                              type="button"
                              className="cp-program-color-section-delete-btn"
                              onClick={() => setDeleteSectionContext(section.id)}
                              aria-label="섹션 삭제"
                              title="섹션 삭제"
                            >
                              <img src="/Trash.svg" alt="삭제" className="track-icon" />
                            </button>
                            <button
                              type="button"
                              className="cp-program-color-section-grip-btn"
                              aria-label="순서 변경"
                              title="드래그하여 순서 변경"
                              {...dragAttributes}
                              {...dragListeners}
                            >
                              <img src="/GripVertical.svg" alt="" className="track-icon" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="cp-section-body">
                      <div className="cp-class-schedule-table-wrap">
                          <table className="cp-class-schedule-table cp-class-schedule-unified-table">
                            <colgroup>
                              <col className="cp-cs-col-seq" />
                              <col className="cp-cs-col-kind" />
                              <col className="cp-cs-col-name" />
                              <col className="cp-cs-col-start" />
                              <col className="cp-cs-col-dur" />
                              <col className="cp-cs-col-end" />
                              <col className="cp-cs-col-period-label" />
                              <col className="cp-cs-col-weekday" />
                              <col className="cp-cs-col-weekday" />
                              <col className="cp-cs-col-weekday" />
                              <col className="cp-cs-col-weekday" />
                              <col className="cp-cs-col-weekday" />
                            </colgroup>
                            <thead>
                              <tr>
                                <th colSpan={6} className="cp-period-table-title-cell">
                                  하루 일과 및 수업시간
                                </th>
                                <th colSpan={6} className="cp-period-table-divider cp-period-table-title-cell">
                                  요일별 기준 수업시수
                                </th>
                              </tr>
                              <tr>
                                <th rowSpan={2}>순</th>
                                <th rowSpan={2}>구분</th>
                                <th rowSpan={2}>이름</th>
                                <th colSpan={3} className="cp-class-schedule-time-mode-header">
                                  <div className="cp-class-schedule-time-mode-bar">
                                    <div
                                      className="cp-class-lesson-alarm-offset-btns cp-class-schedule-time-mode-toggle"
                                      role="group"
                                      aria-label="시각 입력 방식"
                                    >
                                      <button
                                        type="button"
                                        className={`cp-class-lesson-alarm-offset-btn cp-class-schedule-time-mode-btn${
                                          timeMode === 'common' ? ' is-selected' : ''
                                        }`}
                                        aria-pressed={timeMode === 'common'}
                                        onClick={() => setSectionTimeModeForSection(section.id, 'common')}
                                      >
                                        <ClockOutlineIcon />
                                        공통설정
                                      </button>
                                      <ProgramImportLearningDisabledHint hint={weekdayModeHint}>
                                        <button
                                          type="button"
                                          className={`cp-class-lesson-alarm-offset-btn cp-class-schedule-time-mode-btn${
                                            timeMode === 'weekday' ? ' is-selected' : ''
                                          }`}
                                          aria-pressed={timeMode === 'weekday'}
                                          disabled={!canUseWeekdayMode}
                                          onClick={() => setSectionTimeModeForSection(section.id, 'weekday')}
                                        >
                                          <ClockOutlineIcon />
                                          요일별 설정
                                        </button>
                                      </ProgramImportLearningDisabledHint>
                                      {timeMode === 'weekday'
                                        ? WEEKDAY_COLUMNS.map((col) => (
                                            <button
                                              key={`${section.id}-time-${col.key}`}
                                              type="button"
                                              className={`cp-class-lesson-alarm-offset-btn cp-class-schedule-weekday-pick-btn${
                                                editingWeekday === col.key ? ' is-selected' : ''
                                              }`}
                                              onClick={() =>
                                                setSectionEditingWeekday((prev) => ({
                                                  ...prev,
                                                  [section.id]: col.key,
                                                }))
                                              }
                                              aria-label={`${col.label} 시각 편집`}
                                              title={`${col.label} 시각 편집`}
                                            >
                                              {col.label}
                                            </button>
                                          ))
                                        : null}
                                    </div>
                                  </div>
                                </th>
                                <th rowSpan={2} className="cp-period-table-divider">
                                  구분
                                </th>
                                {WEEKDAY_COLUMNS.map((col) => (
                                  <th key={col.key} rowSpan={2} className="cp-class-schedule-weekday-col">
                                    {col.label}
                                  </th>
                                ))}
                              </tr>
                              <tr>
                                <th>
                                  시작시각
                                  {timeMode === 'weekday' ? (
                                    <span className="cp-class-schedule-time-col-hint">
                                      {' '}
                                      ({WEEKDAY_COLUMNS.find((c) => c.key === editingWeekday)?.label})
                                    </span>
                                  ) : null}
                                </th>
                                <th>운영시간(분)</th>
                                <th>종료시각</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row, idx) => {
                                const classCountUpToHere = rows
                                  .slice(0, idx + 1)
                                  .filter((r) => r.kind === 'class').length;
                                const periodLabel = row.kind === 'class' ? `${classCountUpToHere}교시` : '';
                                const rowDisabled = row.kind !== 'class';
                                const rightLabel =
                                  row.kind === 'class'
                                    ? periodLabel
                                    : row.kind === 'activity'
                                      ? '활동'
                                      : row.name || '방과후';
                                const period = idx + 1;
                                return (
                                  <tr
                                    key={`${section.id}-${idx}`}
                                    className={
                                      row.kind === 'activity'
                                        ? 'cp-class-schedule-row-activity'
                                        : row.kind === 'after_school'
                                          ? 'cp-class-schedule-row-after-school'
                                          : ''
                                    }
                                  >
                                    <td className="cp-class-schedule-seq">
                                      <div>{idx + 1}</div>
                                      {periodLabel && (
                                        <div className="cp-class-schedule-period-label">{periodLabel}</div>
                                      )}
                                    </td>
                                    <td>
                                      <select
                                        value={row.kind}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          if (v === 'after_school') {
                                            updateSectionRow(section.id, idx, { kind: 'after_school' });
                                            return;
                                          }
                                          if (v === 'activity') {
                                            updateSectionRow(section.id, idx, { kind: 'activity' });
                                            return;
                                          }
                                          updateSectionRow(section.id, idx, { kind: 'class' });
                                        }}
                                      >
                                        <option value="class">수업</option>
                                        <option value="activity">활동</option>
                                        <option value="after_school">방과후</option>
                                      </select>
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        value={row.name}
                                        onChange={(e) =>
                                          updateSectionRow(section.id, idx, { name: e.target.value })
                                        }
                                        placeholder={
                                          row.kind === 'activity'
                                            ? '예: 아침활동'
                                            : row.kind === 'class'
                                              ? `${periodLabel || '수업'}(자동)`
                                              : '방과후(자동)'
                                        }
                                        readOnly={row.kind !== 'activity'}
                                        disabled={row.kind !== 'activity'}
                                        aria-label={row.kind === 'activity' ? '이름' : '이름 (자동)'}
                                      />
                                    </td>
                                    <td>
                                      <div className="cp-class-schedule-time-cell">
                                        <ClassScheduleTimeInput
                                          value={row.startTime}
                                          onChange={(startTime) =>
                                            updateSectionRow(section.id, idx, { startTime }, 'start')
                                          }
                                          required={row.kind === 'class'}
                                          ariaLabel="시작시각"
                                        />
                                        <button
                                          type="button"
                                          className="cp-time-clear-btn"
                                          onClick={() => updateSectionRow(section.id, idx, { startTime: '' })}
                                          aria-label="시작시각 초기화"
                                          title="초기화"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="cp-class-schedule-time-cell">
                                        <input
                                          type="text"
                                          value={row.durationMinutes}
                                          onChange={(e) =>
                                            updateSectionRow(
                                              section.id,
                                              idx,
                                              {
                                                durationMinutes: e.target.value.replace(/\D/g, '').slice(0, 3),
                                              },
                                              'duration'
                                            )
                                          }
                                          placeholder="분"
                                        />
                                        <button
                                          type="button"
                                          className="cp-time-clear-btn"
                                          onClick={() =>
                                            updateSectionRow(section.id, idx, { durationMinutes: '' })
                                          }
                                          aria-label="운영시간 초기화"
                                          title="초기화"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="cp-class-schedule-time-cell">
                                        <ClassScheduleTimeInput
                                          value={row.endTime}
                                          onChange={(endTime) =>
                                            updateSectionRow(section.id, idx, { endTime }, 'end')
                                          }
                                          ariaLabel="종료시각"
                                        />
                                        <button
                                          type="button"
                                          className="cp-time-clear-btn"
                                          onClick={() =>
                                            updateSectionRow(section.id, idx, { endTime: '' })
                                          }
                                          aria-label="종료시각 초기화"
                                          title="초기화"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    </td>
                                    <td className="cp-period-table-divider cp-period-right-label">
                                      {rightLabel}
                                    </td>
                                    {WEEKDAY_COLUMNS.map((col) => {
                                      const checked = !rowDisabled && period <= weekdayMax[col.key];
                                      return (
                                        <td key={`${section.id}-${col.key}-${period}`} className="cp-class-schedule-weekday-col">
                                          <button
                                            type="button"
                                            className={`cp-period-toggle-btn${checked ? ' is-checked' : ''}`}
                                            disabled={rowDisabled}
                                            onClick={() =>
                                              updateSectionWeekdayMax(section.id, col.key, period)
                                            }
                                            aria-label={`${col.label} ${period}교시까지 활성화`}
                                            title={`${col.label} ${period}교시까지 활성화`}
                                          >
                                            <span className="cp-period-toggle-check">{checked ? 'v' : ''}</span>
                                          </button>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                      </div>
                    </div>
      </article>
                )}
              </SortableSectionGroup>
            );
          })}
        </SortableContext>
      </DndContext>

      {sections.length < MAX_SECTIONS && (
        <button
          type="button"
          className="cp-program-color-add-section-btn"
          onClick={addSection}
          aria-label="섹션 추가"
        >
          <span className="cp-program-color-add-section-btn-icon">+</span>
          <span className="cp-program-color-add-section-btn-label">섹션 추가</span>
        </button>
      )}

      {aiSectionId ? (
        <AiPresetImportModal
          kind="schedule"
          title="AI 자료 입력 · 수업시간 및 요일별 기준 수업시수"
          resumeKey={`schedule:${workspaceId}:${aiSectionId}`}
          defaultGrade={aiDefaultGradeFor(aiSectionId)}
          schoolYear={guessSchoolYear()}
          onClose={() => setAiSectionId(null)}
          onApply={(r) => applyAiSchedule(aiSectionId, r)}
        />
      ) : null}

      {leaderImportTargetSectionId && teamIdForLeaderImport ? (
        <ClassScheduleLeaderSectionImportModal
          open
          teamId={teamIdForLeaderImport}
          targetWorkspaceId={workspaceId}
          targetSectionId={leaderImportTargetSectionId}
          onClose={() => setLeaderImportTargetSectionId(null)}
          onImported={(payload) => applyLeaderClassScheduleImport(leaderImportTargetSectionId, payload)}
        />
      ) : null}

      {copySectionContext && (
        <div
          className="cp-modal-backdrop"
          onClick={() => {
            setCopySectionContext(null);
            setCopyModalSelectedGradeIds([]);
          }}
        >
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>섹션 복사</h3>
            <p className="page-desc">복사할 섹션 데이터를 적용받을 클래스를 선택하세요.</p>
            <div className="cp-program-color-class-picker-list">
              {copyModalAvailableGrades.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className={`cp-program-color-class-picker-item ${copyModalSelectedGradeIds.includes(g.id) ? 'cp-program-color-class-picker-item-selected' : ''}`}
                  onClick={() => toggleCopyModalGrade(g.id)}
                >
                  {parseClassName(g.name).title}
                </button>
              ))}
            </div>
            {copyModalAvailableGrades.length === 0 && (
              <p className="page-desc">등록할 클래스가 없어 복사할 수 없습니다.</p>
            )}
            {copyModalAvailableGrades.length > 0 && copyModalSelectedGradeIds.length === 0 && (
              <p className="page-desc">하나 이상의 클래스를 선택해 주세요.</p>
            )}
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => {
                  setCopySectionContext(null);
                  setCopyModalSelectedGradeIds([]);
                }}
              >
                취소하기
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={handleCopySectionConfirm}
                disabled={
                  sections.length >= MAX_SECTIONS ||
                  copyModalAvailableGrades.length === 0 ||
                  copyModalSelectedGradeIds.length === 0
                }
              >
                복사하기
              </button>
            </div>
          </div>
        </div>
      )}

      {classPickerSectionId && (
        <div className="cp-modal-backdrop" onClick={() => setClassPickerSectionId(null)}>
          <div className="cp-modal cp-class-picker-modal" onClick={(e) => e.stopPropagation()}>
            <h3>클래스 등록</h3>
            <p className="page-desc">이 섹션 데이터를 적용받을 클래스를 선택하세요.</p>
            <div className="cp-program-color-class-picker-list">
              {classPickerAvailableGrades.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    className="cp-program-color-class-picker-item"
                    onClick={() => addClassToSection(classPickerSectionId, g.id)}
                  >
                    {parseClassName(g.name).title}
                  </button>
                ))}
            </div>
            {classPickerAvailableGrades.length === 0 && (
              <p className="page-desc">등록 가능한 클래스가 없습니다.</p>
            )}
            <div className="cp-modal-footer">
              {classPickerAvailableGrades.length === 0 && classSettingsModal ? (
                <button
                  type="button"
                  className="cp-btn-primary"
                  onClick={() => {
                    setClassPickerSectionId(null);
                    classSettingsModal.openClassSettingsModal();
                  }}
                >
                  클래스 등록 바로가기
                </button>
              ) : null}
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setClassPickerSectionId(null)}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {removeClassContext && (
        <div className="cp-modal-backdrop" onClick={() => setRemoveClassContext(null)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>클래스 제거</h3>
        <p className="page-desc">
              이 섹션에서 &quot;
              {grades.find((g) => g.id === removeClassContext.gradeId)
                ? parseClassName(grades.find((g) => g.id === removeClassContext.gradeId)!.name).title
                : ''}
              &quot; 클래스를 제거할까요?
            </p>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setRemoveClassContext(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() =>
                  removeClassFromSection(removeClassContext.sectionId, removeClassContext.gradeId)
                }
              >
                제거
              </button>
            </div>
          </div>
        </div>
      )}

      {resetSectionContext && (
        <div className="cp-modal-backdrop" onClick={() => setResetSectionContext(null)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>섹션 초기화</h3>
            <p className="page-desc">
              이 섹션의 수업시간·요일별 기준 수업시수 데이터와 등록된 클래스를 초기화할까요?
              오른쪽 위 &quot;저장하기&quot;를 눌러야 서버에 반영됩니다.
            </p>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setResetSectionContext(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => resetSectionData(resetSectionContext)}
              >
                초기화
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteSectionContext && (
        <div className="cp-modal-backdrop" onClick={() => setDeleteSectionContext(null)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>섹션 삭제</h3>
            <p className="page-desc">이 섹션을 삭제할까요? 등록된 클래스 연결 정보가 삭제됩니다.</p>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setDeleteSectionContext(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => removeSection(deleteSectionContext)}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        className="cp-fab-save"
        onClick={handleSaveAll}
        disabled={loading || !isDirty}
        aria-label="저장하기"
        title="저장하기"
      >
        <img src="/Save.svg" alt="" className="cp-fab-save-icon" />
      </button>

      <AutoStatusToast state={saveToastState} />
    </section>
  );
};

export default ClassSchedulePage;
